import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import { isEqual } from "lodash-es";

import {
  WebsocketUserDataMessageType,
  WebsocketUserDataType,
} from "../../shared/types/websocket-auth";
import { pageAuthorizationRemoved } from "../../shared/utils";

import { createReplaceAllReducer } from "store/utils";
import { logInfoWithDebugFlag } from "utils/logger";

export type AuthState = {
  user_id: string | null;
  collaboration_jwt: string | null;
  websocket: WebsocketUserDataType;
  // This is mainly used to make sure we don't show 404 when we're loading
  // sections that are from a different org, and instead redirect the user.
  // We set this once at startup from the /users/me endpoint which is cross-org.
  section_to_org_id_map: Record<string, string>;
  should_request_auth?: boolean;
};

const initialState = (): AuthState => ({
  user_id: null,
  collaboration_jwt: null,
  section_to_org_id_map: {},
  websocket: {} as WebsocketUserDataType,
  should_request_auth: false,
});

export const AuthStore = createSlice({
  name: "auth",
  initialState,
  reducers: {
    // Note that setting/clearing the current user will also trigger starting/stopping tracking events in Amplitude
    // if Amplitude is enabled for the current environment.
    setCurrentUser: (state, action: PayloadAction<Omit<AuthState, "websocket"> | null>) => {
      state.should_request_auth = false;
      if (!action.payload) {
        state.user_id = null;
        state.collaboration_jwt = null;
        state.section_to_org_id_map = {};
        return;
      }

      state.user_id = action.payload.user_id;
      state.collaboration_jwt = action.payload.collaboration_jwt;
      state.section_to_org_id_map = action.payload.section_to_org_id_map;
    },
    setShouldRequestAuth: (state, action: PayloadAction<boolean>) => {
      state.should_request_auth = action.payload;
    },
    // We don't set websocket data directly via client or API, instead, Collaboration/Pubsub
    // will send websocket messages to replace the websocket data when it changes in the backend.
    setWebsocketUserData: (state, action: PayloadAction<WebsocketUserDataMessageType>) => {
      const { scope, uniqueId, ...newData } = action.payload;
      // Since we upsert this deeply nested object, we perform a full equality check
      // to prevent re-rendering of components that depend on this state.

      logInfoWithDebugFlag(
        "debug_pubsub",
        `Websocket user auth update for (${uniqueId})`,
        newData,
        { oldPages: state.websocket.pages },
      );

      if (scope === "all") {
        // Reset request_auth flag any time we get a full updated state
        state.should_request_auth = false;
        if (!isEqual(newData.pages, state.websocket.pages)) {
          state.websocket.pages = newData.pages as WebsocketUserDataType["pages"];
        }
      } else {
        // Individual page updates
        Object.keys(newData.pages).forEach((pageId) => {
          const authPage = newData.pages[pageId];

          if (pageAuthorizationRemoved(authPage)) {
            delete state.websocket.pages[pageId];
          } else {
            state.websocket.pages[pageId] = authPage;
          }
        });
      }

      state.websocket.user_id = newData.user_id;
      state.websocket.org_id = newData.org_id;
      state.websocket.section_id = newData.section_id;
      state.websocket.role = newData.role;
      // Only the initial auth message will have a uniqueId
      if (uniqueId) {
        state.websocket.uniqueId = uniqueId;
      }
    },
  },
  extraReducers: createReplaceAllReducer<AuthState>("auth", initialState),
});

export const authStateActions = AuthStore.actions;
